import { useCallback } from 'react';
import { getItem } from './useCookie';
import useLaunchDarklyFlags from './useLaunchDarklyFlags';

export const SESSION_ID_COOKIE = 'USER_SESSION_ID';

const EVENT_SOURCE = 'web';

const USER_IDENTIFIER_TYPE = {
  EMAIL: 'email',
  SESSION_ID: 'session_id',
};

const EVENT_TYPES = {
  TRACK: 'track',
  IDENTIFY: 'identify',
};

// List of all events
export const EVENTS = {
  CATEGORY_VIEW: 'CategoryClicked',
  PRODUCT_VIEW: 'PDPViewed',
  LOCATION_SEARCH: 'LocationSearch',
  EMAIL_SIGN_UP: 'EmailSignup',
  PHONE_SIGN_UP: 'PhoneSignUp',
  GET_DIRECTIONS: 'TapGetDirections',
  VIEW_MENU: 'ViewMenu',
};

export const EMAIL_SIGNUP_CTA = {
  FOOTER: 'Footer',
};

/**
 * Sends Segment data to the java server.
 * @param  {String} eventType  The type of segment api call to  make: EXAMPLE "track"
 * @param  {String} eventName   The name of the event that took place: EXAMPLE "purchase"
 * @param  {object} eventPayload Each payload will de different depending on what the event_name is: EXAMPLE
 *	{
 *	  item_category: 'Top Picks',
 *	  item_name: 'APPLE_CINNAMON_OATS',
 *	  item_number: 4
 *	}
 * @param  {String} userIdentifier  The identifier of the user : EXAMPLE "jsmial@gmail.com"
 * @param  {String} userIdentifierType  The identifier  type of the user: EXAMPLE "email"
 * @return None:
 */
function segmentEventPush(
  eventType,
  eventName,
  eventPayload,
  userIdentifier,
  userIdentifierType,
) {
  const SEGMENT_ENDPOINT_URL =
    window.location.hostname === 'farmersfridge.com' ||
    window.location.hostname === 'www.farmersfridge.com'
      ? 'https://azavv0lkv3.execute-api.us-east-2.amazonaws.com/segment_event'
      : 'https://dqmn4p3fo7.execute-api.us-east-2.amazonaws.com/segment_event';

  fetch(SEGMENT_ENDPOINT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      segmentEventType: eventType,
      eventName,
      eventPayload,
      eventSource: EVENT_SOURCE,
      userIdentifier,
      userIdentifierType,
    }),
  })
    .then(response => response.json())
    .then(json => {
      if (json === false) {
        console.warn('segmentEventPush rejected');
      }
    })
    .catch(err => console.error(`Error 'segmentEventPush': ${err}`));
}

function getUserInfo() {
  const userIdentifier = getItem(SESSION_ID_COOKIE) || '';
  const userIdentifierType = USER_IDENTIFIER_TYPE.SESSION_ID;

  return { userIdentifier, userIdentifierType, sessionId: userIdentifier };
}

/** @param {Object<string,string|object>} payload */
function stringifyPayload(payload) {
  const stringifiedPayload = Object.entries(payload).map(([key, value]) => [
    key,
    typeof value !== 'string' ? JSON.stringify(value) : value,
  ]);
  return Object.fromEntries(stringifiedPayload);
}

/**
 * @param {string} eventType
 * @param {string} eventName
 * @param {any} eventPayload
 */
function pushEvent(eventType, eventName, eventPayload) {
  const { userIdentifier, userIdentifierType, sessionId } = getUserInfo();

  const eventPayloadWithTimestamp = {
    ...stringifyPayload(eventPayload),
    time_stamp: new Date().toISOString(),
    session_id: sessionId,
  };

  segmentEventPush(
    eventType,
    eventName,
    eventPayloadWithTimestamp,
    userIdentifier,
    userIdentifierType,
  );
}

export default function useSegment() {
  const { enableSegment } = useLaunchDarklyFlags();

  const pushTrackEvent = useCallback(
    /**
     * @param {string} eventName
     * @param {any} eventPayload
     */
    (eventName, eventPayload) => {
      if (!enableSegment) {
        return;
      }

      pushEvent(EVENT_TYPES.TRACK, eventName, eventPayload);
    },
    [enableSegment],
  );

  const pushIdentifyEvent = useCallback(
    /**
     * @param {string} eventName
     * @param {any} eventPayload
     */
    (eventName, eventPayload) => {
      if (!enableSegment) {
        return;
      }

      pushEvent(EVENT_TYPES.IDENTIFY, eventName, eventPayload);
    },
    [enableSegment],
  );

  return { pushTrackEvent, pushIdentifyEvent };
}
